import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Minus, Plus, Trash2, ExternalLink, ShoppingBag, Loader2 } from "lucide-react";
import { z } from "zod";
import MainLayout from "@/layouts/MainLayout";
import CheckoutLoading from "@/components/CheckoutLoading";
import FeaturedGamesGrid from "@/components/FeaturedGamesGrid";
import { useCart, CartItem } from "@/contexts/CartContext";
import { useGameCrafterCheckout, TGC_CART_URL } from "@/hooks/useGameCrafterCheckout";
import { supabase } from "@/integrations/supabase/client";
import { Game } from "@/types/game";

const emailSchema = z
  .string()
  .trim()
  .min(3, { message: "Enter your email address" })
  .max(254, { message: "Email must be less than 254 characters" })
  .email({ message: "Enter a valid email address" });

const Cart = () => {
  const { items, subtotal, removeItem, setQuantity, clearCart } = useCart();
  const { sending, checkoutError, fallbackUrl, startCheckout } = useGameCrafterCheckout();

  const { data: availableUpsellGames } = useQuery({
    queryKey: ["cart-upsell-games", items.map((item) => item.gameId)],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("games")
        .select("*, gallery_images:game_gallery_images(*)")
        .eq("is_published", true)
        .eq("featured", true)
        .order("sort_order");
      if (error) throw error;
      const excludeSet = new Set(items.map((item) => item.gameId));
      return (data as unknown as Game[]).filter((game) => !excludeSet.has(game.id));
    },
  });

  const upsellColumns = 3;

  const [email, setEmail] = useState("");
  const [optIn, setOptIn] = useState(true);
  const [emailError, setEmailError] = useState<string | null>(null);

  const emailValid = emailSchema.safeParse(email).success;
  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);

  const handleCheckout = () => {
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setEmailError(parsed.error.issues[0].message);
      return;
    }
    setEmailError(null);
    startCheckout({ email: parsed.data, newsletterOptIn: optIn });
  };

  return (
    <MainLayout>
      <Helmet>
        <title>Your Bag | Rogue Skull Games</title>
        <meta
          name="description"
          content="Review the games in your bag and check out securely through TheGameCrafter."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <section className="bg-white pt-[200px]">
        <div className="container mx-auto">
          <h1 className="text-[hsl(0,0%,10%)] font-bold text-4xl md:text-5xl mb-3">Your Rogue Bag</h1>
          <div className="flex items-center justify-between mb-12">
            <p className="text-[hsl(0,0%,45%)]">
              {itemCount === 0 ? "Nothing here yet." : `${itemCount} item${itemCount === 1 ? "" : "s"} ready to order.`}
            </p>
            <Link
              to="/games"
              className="text-[hsl(0,0%,45%)] hover:text-[hsl(var(--blood-red))] transition-colors uppercase tracking-[0.15em] text-[11px] font-semibold"
            >
              Continue shopping
            </Link>
          </div>

          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-6 py-20 text-center">
              <ShoppingBag className="w-12 h-12 text-[hsl(0,0%,45%)]" />
              <p className="text-[hsl(0,0%,45%)]">Your bag is empty.</p>
              <Link
                to="/games"
                className="inline-flex items-center justify-center border border-[hsl(0,0%,85%)] text-[hsl(0,0%,10%)] hover:bg-[hsl(var(--blood-red))] hover:border-[hsl(var(--blood-red))] hover:text-white transition-all duration-300 px-8 py-4 uppercase tracking-[0.2em] text-xs font-semibold"
              >
                Browse Games
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
              {/* Items — 60% */}
              <div className="lg:col-span-3 space-y-8">
                {items.map((item: CartItem) => (
                  <div key={item.gameId} className="flex gap-5 border-b border-[hsl(0,0%,85%)] pb-8">
                    <Link to={`/games/${item.slug}`} className="shrink-0">
                      <div className="w-32 h-32 sm:w-36 sm:h-36 bg-[hsl(0,0%,96%)] border border-[hsl(0,0%,85%)] overflow-hidden">
                        {item.imageUrl ? (
                          <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover" />
                        ) : null}
                      </div>
                    </Link>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <Link
                          to={`/games/${item.slug}`}
                          className="text-[hsl(0,0%,10%)] text-xl font-semibold leading-tight hover:text-[hsl(var(--blood-red))] transition-colors"
                        >
                          {item.title}
                        </Link>
                        <button
                          type="button"
                          onClick={() => removeItem(item.gameId)}
                          aria-label={`Remove ${item.title} from bag`}
                          className="p-1 text-[hsl(0,0%,45%)] hover:text-[hsl(var(--blood-red))] transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      {item.price != null && item.price > 0 && (
                        <p className="text-[hsl(0,0%,45%)] mt-1">${item.price.toFixed(2)}</p>
                      )}
                      <div className="flex items-center justify-between gap-4 mt-4">
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => setQuantity(item.gameId, item.quantity - 1)}
                            aria-label={`Decrease quantity of ${item.title}`}
                            className="w-8 h-8 border border-[hsl(0,0%,85%)] flex items-center justify-center text-[hsl(0,0%,10%)] hover:border-[hsl(var(--blood-red))] hover:text-[hsl(var(--blood-red))] transition-colors"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="text-[hsl(0,0%,10%)] text-sm w-6 text-center tabular-nums">
                            {item.quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() => setQuantity(item.gameId, item.quantity + 1)}
                            aria-label={`Increase quantity of ${item.title}`}
                            className="w-8 h-8 border border-[hsl(0,0%,85%)] flex items-center justify-center text-[hsl(0,0%,10%)] hover:border-[hsl(var(--blood-red))] hover:text-[hsl(var(--blood-red))] transition-colors"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        {item.price != null && item.price > 0 && (
                          <span className="text-[hsl(0,0%,10%)] text-xl font-semibold">
                            ${(item.price * item.quantity).toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                <div className="flex items-center justify-end pt-2">
                  <button
                    type="button"
                    onClick={clearCart}
                    className="text-[hsl(0,0%,45%)] hover:text-[hsl(var(--blood-red))] transition-colors uppercase tracking-[0.15em] text-[11px] font-semibold"
                  >
                    Clear your bag
                  </button>
                </div>
              </div>

              {/* Summary — 40% */}
              <aside className="lg:col-span-2">
                <div className="lg:sticky lg:top-[105px] border border-[hsl(0,0%,85%)] bg-[hsl(0,0%,98%)] p-6 sm:p-8">
                  {sending ? (
                    <div className="py-10">
                      <CheckoutLoading compact />
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between text-[hsl(0,0%,10%)] mb-2">
                        <span className="uppercase tracking-[0.15em] text-xl font-semibold">Subtotal</span>
                        <span className="text-2xl font-bold">${subtotal.toFixed(2)}</span>
                      </div>
                      <p className="text-[hsl(0,0%,45%)] text-xs leading-relaxed mb-8">
                        Shipping and tax are calculated at TheGameCrafter, where your order is completed.
                      </p>

                      <label
                        htmlFor="checkout-email"
                        className="block text-[hsl(0,0%,10%)] text-xs uppercase tracking-[0.15em] font-semibold mb-2"
                      >
                        Email address
                      </label>
                      <input
                        id="checkout-email"
                        type="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (emailError) setEmailError(null);
                        }}
                        maxLength={254}
                        autoComplete="email"
                        placeholder="you@example.com"
                        aria-invalid={Boolean(emailError)}
                        aria-describedby={emailError ? "checkout-email-error" : "checkout-email-help"}
                        className="w-full bg-white border border-[hsl(0,0%,85%)] text-[hsl(0,0%,10%)] placeholder:text-[hsl(0,0%,55%)] px-4 py-3 text-sm focus:outline-none focus:border-[hsl(var(--blood-red))] transition-colors"
                      />
                      <p id="checkout-email-help" className="text-[hsl(0,0%,45%)] text-[11px] mt-2 leading-relaxed">
                        We use this to follow up on your order. Required to continue.
                      </p>
                      {emailError && (
                        <p id="checkout-email-error" role="alert" className="text-[hsl(var(--blood-red))] text-xs mt-2">
                          {emailError}
                        </p>
                      )}

                      <label className="flex items-start gap-3 mt-5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={optIn}
                          onChange={(e) => setOptIn(e.target.checked)}
                          className="mt-0.5 w-4 h-4 accent-[hsl(var(--blood-red))]"
                        />
                        <span className="text-[hsl(0,0%,45%)] text-xs leading-relaxed">
                          Send me news about new games and releases from Rogue Skull Games.
                        </span>
                      </label>

                      <button
                        type="button"
                        onClick={handleCheckout}
                        disabled={sending || !emailValid}
                        className="w-full mt-6 inline-flex items-center justify-center gap-2 bg-[hsl(var(--blood-red))] text-white border border-[hsl(var(--blood-red))] hover:bg-transparent hover:text-[hsl(0,0%,10%)] transition-all duration-300 px-6 py-4 uppercase tracking-[0.2em] text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[hsl(var(--blood-red))] disabled:hover:text-white"
                      >
                        {sending ? "Opening Game Crafter checkout…" : "Checkout at TheGameCrafter"}
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                      </button>

                      {checkoutError && (
                        <>
                          <p
                            role="alert"
                            className="text-[hsl(var(--blood-red))] text-xs leading-relaxed text-center mt-4"
                          >
                            {checkoutError}
                          </p>
                          <a
                            href={fallbackUrl ?? TGC_CART_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full mt-3 inline-flex items-center justify-center gap-2 border border-[hsl(0,0%,85%)] text-[hsl(0,0%,10%)] hover:bg-[hsl(var(--blood-red))] hover:border-[hsl(var(--blood-red))] hover:text-white transition-all duration-300 px-6 py-3 uppercase tracking-[0.2em] text-[11px] font-semibold"
                          >
                            {fallbackUrl ? "Continue to your cart" : "Open TheGameCrafter cart"}
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </>
                      )}
                    </>
                  )}
                </div>
              </aside>
            </div>
          )}
        </div>
      </section>

      {availableUpsellGames && availableUpsellGames.length > 0 && (
        <FeaturedGamesGrid
          games={availableUpsellGames}
          sectionLabel="Keep the Adventure Going"
          sectionTitle="You Might Also Like"
          viewAllHref="/games"
          viewAllLabel="Browse All Games"
          forceColumns={upsellColumns}
          maxItems={upsellColumns}
          showAddToBag={true}
        />
      )}
    </MainLayout>
  );
};

export default Cart;
